import React, { useContext } from 'react';
import { Empty, Typography } from 'antd';

import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { isNotNull } from '@totem/utilities/common';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Step parser
//
// Splits stepsRequired into discrete step objects when the content is
// structured (numbered list, bullet list). Falls back to treating each
// non-empty line as a plain paragraph.
// ---------------------------------------------------------------------------

type ParsedStep = {
  index: number;
  text: string;
};

const NUMBERED_RE = /^\s*(\d+)[.)]\s+(.+)/;
const BULLET_RE = /^\s*[-•*]\s+(.+)/;

const parseSteps = (
  raw: string,
): { steps: ParsedStep[]; structured: boolean } => {
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const isNumbered = lines.every((line) => NUMBERED_RE.test(line));
  const isBulleted = !isNumbered && lines.every((line) => BULLET_RE.test(line));

  if (isNumbered) {
    return {
      structured: true,
      steps: lines.map((line, idx) => {
        const match = line.match(NUMBERED_RE);
        return {
          index: match ? parseInt(match[1], 10) : idx + 1,
          text: match ? match[2] : line,
        };
      }),
    };
  }

  if (isBulleted) {
    return {
      structured: true,
      steps: lines.map((line, idx) => {
        const match = line.match(BULLET_RE);
        return { index: idx + 1, text: match ? match[1] : line };
      }),
    };
  }

  // Unstructured — each line becomes a plain paragraph step
  return {
    structured: false,
    steps: lines.map((line, idx) => ({ index: idx + 1, text: line })),
  };
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StepCard = ({ step, isLast }: { step: ParsedStep; isLast: boolean }) => (
  <div style={{ display: 'flex', gap: 16, paddingBottom: isLast ? 0 : 20 }}>
    {/* Number + connector line */}
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: '#F3F4F6',
          border: '2px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 700,
          color: '#374151',
          flexShrink: 0,
        }}
      >
        {step.index}
      </div>
      {!isLast && (
        <div
          style={{
            width: 2,
            flex: 1,
            background: '#E5E7EB',
            marginTop: 4,
            minHeight: 16,
          }}
        />
      )}
    </div>

    {/* Step text */}
    <div style={{ paddingTop: 4, paddingBottom: isLast ? 0 : 4, flex: 1 }}>
      <Text style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
        {step.text}
      </Text>
    </div>
  </div>
);

const ParagraphBlock = ({ steps }: { steps: ParsedStep[] }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    {steps.map((step) => (
      <Text
        key={step.index}
        style={{
          fontSize: 14,
          color: '#374151',
          lineHeight: 1.7,
          display: 'block',
        }}
      >
        {step.text}
      </Text>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const OpportunityPlanTab = () => {
  const { ticket } = useContext(TicketContext);

  if (!ticket) return null;

  const opt = ticket.ticket.extras?.optimize ?? null;
  const stepsRequired = isNotNull(opt) ? opt.stepsRequired?.trim() : '';

  if (!stepsRequired) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No implementation plan has been entered for this opportunity."
        style={{ paddingTop: 32 }}
      />
    );
  }

  const { steps, structured } = parseSteps(stepsRequired);

  return (
    <div style={{ maxWidth: 680 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: '#111827',
            marginBottom: 4,
          }}
        >
          Implementation plan
        </div>
        {isNotNull(opt) && (
          <Text style={{ fontSize: 13, color: '#6B7280' }}>
            {opt.levelOfEffort} effort
            {opt.category ? ` · ${opt.category}` : ''}
          </Text>
        )}
      </div>

      {/* Steps */}
      {structured ? (
        <div
          style={{
            background: '#FAFAFA',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            padding: '20px 24px',
          }}
        >
          {steps.map((step, idx) => (
            <StepCard
              key={step.index}
              step={step}
              isLast={idx === steps.length - 1}
            />
          ))}
        </div>
      ) : (
        <div
          style={{
            background: '#FAFAFA',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            padding: '20px 24px',
          }}
        >
          <ParagraphBlock steps={steps} />
        </div>
      )}
    </div>
  );
};

export default OpportunityPlanTab;
